import React, { useState } from 'react';
import { Shield, CheckCircle, Zap, Lock, Server } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { sonicWallFirewalls } from '../mock';

const SonicWall = () => {
  const { toast } = useToast();
  const [quoteFirewall, setQuoteFirewall] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: ''
  });

  const handleQuote = (firewall) => {
    setQuoteFirewall(firewall);
    setShowQuoteForm(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/sonicwall-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: quoteFirewall.model,
          series: quoteFirewall.series,
          price: quoteFirewall.price,
          recommended: quoteFirewall.recommended,
          ...quoteFormData
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Quote Request Submitted!",
          description: data.message || `We'll contact you shortly about the ${quoteFirewall.model}.`,
        });
        setShowQuoteForm(false);
        setQuoteFormData({ customerName: '', customerEmail: '', customerPhone: '' });
      } else {
        throw new Error(data.detail || 'Failed to submit quote request');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again or call us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Background Image */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxuZXR3b3JrJTIwc2VjdXJpdHl8ZW58MHx8fHwxNzYwNTczMTQwfDA&ixlib=rb-4.1.0&q=85"
            alt="Network Security"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/95 via-orange-900/90 to-red-800/90"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6 text-white">
            <Badge className="bg-orange-400 text-red-900 hover:bg-orange-400 border-orange-300">
              Enterprise Security Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              SonicWall Firewalls
              <span className="block text-orange-300 mt-2">Enterprise-Grade Protection</span>
            </h1>
            <p className="text-xl text-red-100 max-w-2xl mx-auto">
              Protect your business with industry-leading network security solutions. From small offices to large enterprises.
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why SonicWall?</h2>
            <p className="text-lg text-gray-600">Industry-leading protection trusted by over 500,000 organizations worldwide</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 group hover:bg-red-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                <Shield className="h-8 w-8 text-red-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Advanced Threat Protection</h3>
              <p className="text-sm text-gray-600">Real-time deep packet inspection and multi-engine sandboxing</p>
            </div>

            <div className="text-center p-6 group hover:bg-red-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                <Zap className="h-8 w-8 text-red-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">High Performance</h3>
              <p className="text-sm text-gray-600">Multi-gigabit throughput without compromising security</p>
            </div>

            <div className="text-center p-6 group hover:bg-red-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                <Lock className="h-8 w-8 text-red-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">SSL/TLS Inspection</h3>
              <p className="text-sm text-gray-600">Decrypt and inspect encrypted traffic for hidden threats</p>
            </div>

            <div className="text-center p-6 group hover:bg-red-50 rounded-lg transition-colors">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-red-600 transition-colors">
                <Server className="h-8 w-8 text-red-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Centralized Management</h3>
              <p className="text-sm text-gray-600">Single-pane-of-glass management for all devices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-red-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Protection Level</h2>
            <p className="text-lg text-gray-600">Select the perfect SonicWall solution for your business size and security needs</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {sonicWallFirewalls.map((firewall) => (
              <Card key={firewall.id} className="hover:shadow-2xl transition-all duration-300 border-2 hover:border-red-300 hover:-translate-y-1 overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={firewall.image}
                    alt={`${firewall.series} ${firewall.model}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <Badge className="absolute top-4 right-4 bg-red-600 text-white">{firewall.series}</Badge>
                </div>
                <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl mb-2">{firewall.model}</CardTitle>
                      <CardDescription className="text-sm">{firewall.description}</CardDescription>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="text-3xl font-bold text-gray-900">
                      ${firewall.price.toLocaleString()}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {firewall.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="flex gap-2">
                  <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={() => handleQuote(firewall)}>
                    Request Quote
                  </Button>
                  <Button variant="outline" className="flex-1">
                    Learn More
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Guide */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Learn more about SonicWall firewalls and how they protect your business</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="font-semibold">What makes SonicWall different from other firewalls?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                SonicWall offers patented Real-Time Deep Memory Inspection (RTDMI) technology that detects and blocks advanced threats including ransomware, zero-day attacks, and encrypted malware. Their multi-engine sandboxing provides superior protection compared to traditional firewalls.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="font-semibold">Which SonicWall model is right for my business?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Choose based on your user count: TZ270 for 1-10 users, TZ470 for 10-50 users, NSa 2700 for 50-250 users, and NSsp 12400 for enterprises with 250+ users. Consider your bandwidth requirements and whether you need advanced features like multi-instance capability.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="font-semibold">What's included in the price?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                The listed price includes the hardware and 1-year security services subscription (Gateway Anti-Virus, Anti-Spyware, Intrusion Prevention, Application Control, and Content Filtering). We also provide free installation support and initial configuration assistance.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="font-semibold">Do you offer installation and ongoing support?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Yes! We provide professional installation, configuration, and training. Our support plans include 24/7 monitoring, firmware updates, policy optimization, and immediate response to security incidents. Contact us for custom support packages.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline">
                <span className="font-semibold">Can I upgrade or add more features later?</span>
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                Absolutely! SonicWall firewalls are designed to grow with your business. You can add advanced features like Advanced Threat Protection, Capture ATP with RTDMI, Network Security Manager, and additional security services as your needs evolve.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-orange-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Secure Your Network?</h2>
          <p className="text-xl mb-8 text-red-100 max-w-2xl mx-auto">
            Contact our security experts to find the perfect SonicWall solution for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Schedule Consultation
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 bg-transparent border-white text-white hover:bg-white hover:text-red-600">
              Download Datasheet
            </Button>
          </div>
        </div>
      </section>

      {/* Quote Request Dialog */}
      <Dialog open={showQuoteForm} onOpenChange={setShowQuoteForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request SonicWall Quote</DialogTitle>
            <DialogDescription>
              {quoteFirewall && `${quoteFirewall.series} ${quoteFirewall.model} - $${quoteFirewall.price.toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuoteSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Your Name</Label>
              <Input
                id="customerName"
                placeholder="John Doe"
                value={quoteFormData.customerName}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">Email Address</Label>
              <Input
                id="customerEmail"
                type="email"
                placeholder="john@example.com"
                value={quoteFormData.customerEmail}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerEmail: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerPhone">Phone Number</Label>
              <Input
                id="customerPhone"
                type="tel"
                placeholder="(555) 123-4567"
                value={quoteFormData.customerPhone}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerPhone: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuoteForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">Submit Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SonicWall;